// KixiePowerDialPanel — Settings card for the BYO-Kixie integration.
//
// Lives under Settings → Comms & Calendar. Three states:
//   · loading      — initial fetch of /api/kixie/status
//   · not_connected — Door A (Get Kixie via partner link) +
//                     Door B (paste API Key + Business ID)
//   · connected    — webhook URL, business id, powerlist id, status
//                     metadata, Disconnect button
//
// The plaintext API key is type="password" and is sent ONCE on
// connect — the response never echoes it back, so re-edit means
// re-paste. /api/kixie/status reads metadata only (decrypt:false),
// so the plaintext never crosses the wire after the initial paste.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

// Kixie partner template (their required format):
//   https://www.kixie.com/?utm_medium=website&utm_source=partner_affiliate&utm_campaign={partner_key}
// {partner_key} is the per-partner identifier Kixie assigns at
// approval. We read it from REACT_APP_KIXIE_PARTNER_KEY so it can
// be swapped without code change once Kixie issues ours. Until then
// the fallback ('sixfigureagentcrm') tags clicks with our name so
// Kixie can still trace attribution to us by hand.
const KIXIE_PARTNER_KEY =
  (typeof process !== 'undefined' && process.env?.REACT_APP_KIXIE_PARTNER_KEY) ||
  'sixfigureagentcrm';
const KIXIE_PARTNER_URL = `https://www.kixie.com/?utm_medium=website&utm_source=partner_affiliate&utm_campaign=${encodeURIComponent(KIXIE_PARTNER_KEY)}`;

async function bearerHeaders() {
  try {
    const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

function webhookUrl(token) {
  if (!token) return '';
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) ||
    'https://app.sixfigureagentcrm.com';
  return `${origin}/api/kixie/webhook/${token}`;
}

function StatusBadge({ status }) {
  const map = {
    connected:    { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'Connected' },
    new:          { bg: 'bg-slate-100',   fg: 'text-slate-700',   label: 'Not connected' },
    referred:     { bg: 'bg-violet-100',  fg: 'text-violet-800',  label: 'Signup in progress' },
    invalid:      { bg: 'bg-red-100',     fg: 'text-red-800',     label: 'Key invalid — reconnect' },
    disconnected: { bg: 'bg-slate-100',   fg: 'text-slate-600',   label: 'Disconnected' },
  };
  const s = map[status] || map.new;
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function AgentEmailMappingForm({ currentEmail, onSave, onClear, busy, error }) {
  const [email, setEmail] = React.useState(currentEmail || '');
  React.useEffect(() => { setEmail(currentEmail || ''); }, [currentEmail]);

  const submit = (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
    onSave(v);
  };

  return (
    <div className={`${currentEmail ? 'bg-slate-50 border-slate-200' : 'bg-sky-50 border-sky-200'} border rounded-lg p-3`}>
      <div className={`text-xs font-bold ${currentEmail ? 'text-slate-700' : 'text-sky-900'} mb-1`}>
        Step 3 — Your Kixie agent email
      </div>
      <div className={`text-[11px] ${currentEmail ? 'text-slate-600' : 'text-sky-800'} mb-2`}>
        Routes incoming call results back to your CRM timeline. Use the same email you sign in to Kixie with.
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 flex-wrap">
        <input
          type="email"
          autoComplete="off"
          name="kx-agent-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourdomain.com"
          className="flex-1 min-w-[220px] text-sm bg-white border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-500"
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="bg-sky-700 hover:bg-sky-800 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded"
        >
          {busy ? 'Saving…' : currentEmail ? 'Update' : 'Save email'}
        </button>
        {currentEmail && (
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="text-xs text-slate-500 hover:text-slate-900 font-medium px-2"
          >
            Clear
          </button>
        )}
      </form>
      {error && (
        <div className="text-[11px] text-red-800 mt-2">Couldn't save: {error}</div>
      )}
    </div>
  );
}

function PowerlistDesignationForm({ onSave, busy, error }) {
  const [pid, setPid] = React.useState('');
  const submit = (e) => {
    e.preventDefault();
    if (!pid.trim()) return;
    onSave(pid.trim());
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
      <div className="text-xs font-bold text-amber-900 mb-1">Step 2 — Designate a Powerlist</div>
      <div className="text-[11px] text-amber-800 mb-2">
        SFA pushes leads to one Powerlist on your Kixie account. Create or pick one
        in Kixie → Powerlists, then paste its ID here.
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          autoComplete="off"
          name="kx-powerlist-id"
          value={pid}
          onChange={(e) => setPid(e.target.value)}
          placeholder="Powerlist ID"
          className="flex-1 min-w-[200px] text-sm bg-white border border-amber-300 rounded-lg px-3 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy || !pid.trim()}
          className="bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded"
        >
          {busy ? 'Saving…' : 'Save Powerlist'}
        </button>
      </form>
      {error && (
        <div className="text-[11px] text-red-800 mt-2">Couldn't save: {error}</div>
      )}
    </div>
  );
}

function ConnectedView({ conn, agentEmail, onDisconnect, onCopy, copied, onSavePowerlist, plBusy, plError, onSaveAgentEmail, onClearAgentEmail, agentBusy, agentError }) {
  const url = webhookUrl(conn.webhook_token);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">Kixie power dialer</div>
          <div className="text-xs text-slate-500 mt-0.5">Connected to your Kixie account. Power dialing from this CRM runs through your Kixie Powerlist.</div>
        </div>
        <StatusBadge status="connected"/>
      </div>

      {!conn.powerlist_id && (
        <PowerlistDesignationForm onSave={onSavePowerlist} busy={plBusy} error={plError}/>
      )}

      <AgentEmailMappingForm
        currentEmail={agentEmail}
        onSave={onSaveAgentEmail}
        onClear={onClearAgentEmail}
        busy={agentBusy}
        error={agentError}
      />


      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Webhook URL — paste into Kixie → Integrations → Webhooks</div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono text-slate-900 max-w-full overflow-x-auto whitespace-nowrap">{url}</code>
          <button
            onClick={() => onCopy(url)}
            className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded"
          >
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        </div>
        <div className="text-[11px] text-slate-500">Register the <strong>End Call</strong> and <strong>Disposition</strong> events at minimum. Kixie will POST call results here, attributed to your account via this token.</div>
      </div>

      <dl className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="text-slate-500 font-semibold">Business ID</dt>
          <dd className="text-slate-900 font-mono mt-0.5">{conn.business_id || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500 font-semibold">Powerlist ID</dt>
          <dd className="text-slate-900 font-mono mt-0.5">{conn.powerlist_id || <em className="text-amber-700 not-italic">Designate above</em>}</dd>
        </div>
        <div>
          <dt className="text-slate-500 font-semibold">Connected</dt>
          <dd className="text-slate-700 mt-0.5">{conn.connected_at ? new Date(conn.connected_at).toLocaleString() : '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500 font-semibold">Recording attestation</dt>
          <dd className="text-slate-700 mt-0.5">{conn.recording_attested_at ? '✓ on file' : <span className="text-amber-700">Not attested</span>}</dd>
        </div>
      </dl>

      <div className="pt-1">
        <button
          onClick={onDisconnect}
          className="text-xs text-red-700 hover:text-red-900 font-semibold underline"
        >
          Disconnect Kixie
        </button>
      </div>
    </div>
  );
}

function ConnectForm({ onConnect, busy, error, fix }) {
  const [apiKey, setApiKey]         = React.useState('');
  const [businessId, setBusinessId] = React.useState('');
  const [powerlistId, setPowerlistId] = React.useState('');
  const [attest, setAttest]         = React.useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!apiKey.trim() || !businessId.trim()) return;
    onConnect({
      apiKey: apiKey.trim(),
      businessId: businessId.trim(),
      powerlistId: powerlistId.trim() || undefined,
      recordingAttested: attest,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="text-xs font-semibold text-slate-700">Kixie API Key</label>
        <input
          type="password"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          name="kx-api-key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Paste from Kixie → Manage → Account Settings → Integrations"
          className="mt-1 w-full text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
          disabled={busy}
        />
        <div className="text-[11px] text-slate-500 mt-1">Your key is encrypted at rest (Supabase Vault) and never returned to the browser again.</div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700">Business ID</label>
        <input
          type="text"
          autoComplete="off"
          name="kx-business-id"
          value={businessId}
          onChange={(e) => setBusinessId(e.target.value)}
          placeholder="e.g. 1234567"
          className="mt-1 w-full text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
          disabled={busy}
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700">Powerlist ID (optional — required before first dial)</label>
        <input
          type="text"
          autoComplete="off"
          name="kx-powerlist-id"
          value={powerlistId}
          onChange={(e) => setPowerlistId(e.target.value)}
          placeholder="From the Powerlist you'll use for SFA dialing"
          className="mt-1 w-full text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
          disabled={busy}
        />
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={attest}
          onChange={(e) => setAttest(e.target.checked)}
          className="mt-0.5"
          disabled={busy}
        />
        <span className="text-xs text-slate-700">
          I understand recording-consent law varies by state (one-party / all-party) and I'm
          responsible for compliance with whatever Kixie recording settings I have on my account.
        </span>
      </label>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-900">
          <div className="font-semibold">Couldn't connect: {error}</div>
          {fix && <div className="text-red-800 mt-1">{fix}</div>}
        </div>
      )}

      <button
        type="submit"
        disabled={busy || !apiKey.trim() || !businessId.trim()}
        className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-lg"
      >
        {busy ? 'Connecting…' : 'Connect Kixie'}
      </button>
    </form>
  );
}

export default function KixiePowerDialPanel({ showToast = () => {} }) {
  const [state, setState] = React.useState('loading'); // loading | not_connected | connected
  const [conn, setConn]   = React.useState(null);
  const [error, setError] = React.useState(null);
  const [fix, setFix]     = React.useState(null);
  const [busy, setBusy]   = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [plBusy, setPlBusy] = React.useState(false);
  const [plError, setPlError] = React.useState(null);
  const [agentEmail, setAgentEmail] = React.useState('');
  const [agentBusy, setAgentBusy] = React.useState(false);
  const [agentError, setAgentError] = React.useState(null);

  const reloadAgentEmail = React.useCallback(async () => {
    try {
      const r = await fetch('/api/kixie/agents', { headers: await bearerHeaders() });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body?.ok && Array.isArray(body.mappings)) {
        // Self-mapping: the row whose user_id equals our auth uid.
        // We don't have auth uid client-side here, so we take the
        // first row owned by us (the API filters to owner_id =
        // caller, so all returned rows belong to this operator).
        // Phase 6 only ships self-map, so the first row IS us.
        const mine = body.mappings[0];
        setAgentEmail(mine?.kixie_agent_email || '');
      }
    } catch { /* swallow — agent mapping is supplementary */ }
  }, []);

  const reload = React.useCallback(async () => {
    setState((s) => (s === 'connected' ? s : 'loading'));
    try {
      const r = await fetch('/api/kixie/status', { headers: await bearerHeaders() });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        setError(body?.error || `status_${r.status}`);
        setState('not_connected');
        return;
      }
      setError(null); setFix(null);
      if (body.connected && body.connection) {
        setConn(body.connection);
        setState('connected');
        // Surface the operator's saved agent email mapping when the
        // panel is rendered in the connected state.
        reloadAgentEmail();
      } else {
        setConn(body.connection || null);
        setState('not_connected');
      }
    } catch (e) {
      setError(String(e?.message || e));
      setState('not_connected');
    }
  }, [reloadAgentEmail]);

  React.useEffect(() => { reload(); }, [reload]);

  const onConnect = async (payload) => {
    setBusy(true); setError(null); setFix(null);
    try {
      const r = await fetch('/api/kixie/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
        body: JSON.stringify(payload),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        setError(body?.error || `connect_${r.status}`);
        setFix(body?.fix || null);
        showToast('Kixie connect failed — see the form for details.', 'error');
        return;
      }
      showToast('Kixie connected. Power dialing is ready.');
      await reload();
    } finally { setBusy(false); }
  };

  const onDisconnect = async () => {
    if (!window.confirm('Disconnect Kixie? Your CRM will stop pushing to your Powerlist. You can reconnect anytime.')) return;
    setBusy(true);
    try {
      const r = await fetch('/api/kixie/disconnect', {
        method: 'POST',
        headers: await bearerHeaders(),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        showToast(`Disconnect failed: ${body?.error || r.status}`, 'error');
        return;
      }
      showToast('Kixie disconnected.');
      await reload();
    } finally { setBusy(false); }
  };

  const onSavePowerlist = async (powerlistId) => {
    setPlBusy(true); setPlError(null);
    try {
      const r = await fetch('/api/kixie/powerlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
        body: JSON.stringify({ powerlistId }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        const msg = body?.error || `save_${r.status}`;
        setPlError(msg);
        showToast(`Powerlist save failed: ${msg}`, 'error');
        return;
      }
      showToast('Powerlist designated. You can now power-dial leads.');
      await reload();
    } finally { setPlBusy(false); }
  };

  const onSaveAgentEmail = async (email) => {
    setAgentBusy(true); setAgentError(null);
    try {
      const r = await fetch('/api/kixie/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
        body: JSON.stringify({ kixie_agent_email: email }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        const msg = body?.error || `save_${r.status}`;
        setAgentError(msg);
        showToast(`Couldn't save agent email: ${msg}`, 'error');
        return;
      }
      showToast('Kixie agent email saved.');
      await reloadAgentEmail();
    } finally { setAgentBusy(false); }
  };

  const onClearAgentEmail = async () => {
    if (!window.confirm('Clear your Kixie agent email mapping? Call results will stop routing to your timeline until you re-save.')) return;
    setAgentBusy(true); setAgentError(null);
    try {
      const r = await fetch('/api/kixie/agents', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
        body: JSON.stringify({}),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body?.ok) {
        const msg = body?.error || `clear_${r.status}`;
        setAgentError(msg);
        showToast(`Couldn't clear: ${msg}`, 'error');
        return;
      }
      setAgentEmail('');
      showToast('Agent email cleared.');
    } finally { setAgentBusy(false); }
  };

  const onCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Copy failed — long-press the URL to copy manually.', 'error');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      {state === 'loading' && (
        <div className="text-sm text-slate-500">Checking Kixie connection…</div>
      )}

      {state === 'not_connected' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-slate-900">Kixie power dialer (optional)</div>
              <div className="text-xs text-slate-500 mt-0.5">Connect your Kixie account to power-dial your CRM leads. You bring your own Kixie subscription — SFA never sees your billing or numbers.</div>
            </div>
            <StatusBadge status={conn?.status || 'new'}/>
          </div>

          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <div className="text-xs font-bold text-violet-900 mb-1">Do you already have Kixie?</div>
            <div className="flex items-center gap-3 flex-wrap mt-2">
              <a
                href={KIXIE_PARTNER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={async () => {
                  // Best-effort attribution stamp before opening Kixie.
                  // Failures here MUST NOT block the navigation — the
                  // worst case is we just miss an attribution event.
                  try {
                    await fetch('/api/kixie/refer', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(await bearerHeaders()) },
                      body: JSON.stringify({ ref_token: KIXIE_PARTNER_KEY }),
                    });
                  } catch { /* swallow */ }
                }}
                className="bg-white border border-violet-300 hover:bg-violet-100 text-violet-800 text-xs font-semibold px-3 py-1.5 rounded"
              >
                No — Get Kixie ↗
              </a>
              <span className="text-[11px] text-violet-700">Then come back here to paste your key.</span>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-bold text-slate-700 mb-2">Yes — paste your credentials</div>
            <ConnectForm onConnect={onConnect} busy={busy} error={error} fix={fix}/>
          </div>
        </div>
      )}

      {state === 'connected' && conn && (
        <ConnectedView
          conn={conn}
          onDisconnect={onDisconnect}
          onCopy={onCopy}
          copied={copied}
          onSavePowerlist={onSavePowerlist}
          plBusy={plBusy}
          plError={plError}
          agentEmail={agentEmail}
          onSaveAgentEmail={onSaveAgentEmail}
          onClearAgentEmail={onClearAgentEmail}
          agentBusy={agentBusy}
          agentError={agentError}
        />
      )}
    </div>
  );
}
